import React, { FC, useState } from 'react';
import { PlayerInfo } from '@/screens/authenticated/types';
import { findPlayer } from '../lib/turn';
import { Modal } from './modal';

export interface ResultsPanelProps {
  players: PlayerInfo[];
  loser: string | null;
  draw?: boolean;
  meID: string;
  isHost: boolean;
  hostName?: string;
  onRematch: () => Promise<void>;
  onLeave: () => void;
  onNewTable: () => void;
  onClose: () => void;
}

export const ResultsPanel: FC<ResultsPanelProps> = ({
  players,
  loser,
  draw = false,
  meID,
  isHost,
  hostName,
  onRematch,
  onLeave,
  onNewTable,
  onClose,
}) => {
  const [busy, setBusy] = useState(false);
  const bhabi = findPlayer(players, loser);
  const iAmBhabi = bhabi?.id === meID;
  const standings = [...players].sort((a, b) => {
    if (a.id === bhabi?.id) return 1;
    if (b.id === bhabi?.id) return -1;
    return a.entryNo - b.entryNo;
  });

  return (
    <Modal
      title={
        draw
          ? 'Everyone escaped!'
          : iAmBhabi
            ? 'You are the Bhabi!'
            : `${bhabi?.name || loser || 'Someone'} is the Bhabi!`
      }
      onClose={onClose}
      suit={iAmBhabi ? 'hearts' : 'spades'}
    >
      <p className="st-muted">
        {draw
          ? 'Every hand is empty. No Bhabi this time — deal again?'
          : iAmBhabi
            ? 'Last one holding cards this time. The table demands a rematch.'
            : 'Everyone else emptied their hand and escaped.'}
      </p>

      <ul className="st-standings" aria-label="Final standings">
        {standings.map(p => {
          const isBhabi = p.id === bhabi?.id;
          return (
            <li key={p.id} data-bhabi={isBhabi ? 'true' : undefined}>
              <span>
                {p.name}
                {p.id === meID ? ' (you)' : ''}
              </span>
              <span className={`st-tag ${isBhabi ? 'st-tag--bhabi' : ''}`}>
                {isBhabi ? `Bhabi · ${p.cardsLeft} left` : 'Escaped'}
              </span>
            </li>
          );
        })}
      </ul>

      {isHost ? (
        <button
          type="button"
          className="st-btn st-btn--primary st-btn--block"
          disabled={busy}
          aria-busy={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onRematch();
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Shuffling…' : 'Rematch with the same table'}
        </button>
      ) : (
        <p className="st-small st-muted" role="status">
          Waiting for {hostName || 'the host'} to deal a rematch. Stay seated to
          play again.
        </p>
      )}
      <div className="st-row">
        <button
          type="button"
          className="st-btn st-btn--on-ivory"
          style={{ flex: 1 }}
          onClick={onNewTable}
        >
          New table
        </button>
        <button
          type="button"
          className="st-btn st-btn--on-ivory"
          style={{ flex: 1 }}
          onClick={onLeave}
        >
          Leave
        </button>
      </div>
      <button type="button" className="st-link st-small" onClick={onClose}>
        View the table
      </button>
    </Modal>
  );
};
